import React, { useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { AppContext } from '@/AppContext';
import { useParams } from 'react-router-dom';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import AntSwitch from '@/components/AntSwitch/AntSwitch';
import FormFields from './FormFields';
import MatchingSettings from './MatchingSettings';
import Switch from '@mui/material/Switch';
import CardBlock from '@/components/CardBlock/CardBlock';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormLabel from '@mui/material/FormLabel';
import { useForm, Controller } from 'react-hook-form';
import { ECTextField } from '@/components/Forms/ECTextField';
import { ECPhotoField } from '@/components/Forms/ECPhotoField';
import { ECRadioGroupField } from '@/components/Forms/ECRadioGroupField';
import { ECSelectField } from '@/components/Forms/ECSelectField';
import { TextField, Button, IconButton, Divider, MenuItem, Checkbox } from '@mui/material';
import { errorsToForm } from '@/components/Forms/form_utils';
import { NetworkingScenarioType, SystemFieldType } from '@/api/api';
import { fieldFullName } from '@/views/VisitorList/components/ImportXLS/import_utils';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const PREFIX = 'Networking';

const classes = {
  root: `${PREFIX}-root`,
  topBar: `${PREFIX}-topBar`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    marginTop: 24,
  },

  [`& .${classes.topBar}`]: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

const ProfileFieldsSection = ({ form, settings, availableFields, handleSave }) => {
  const profileFields = form.watch('profileFields') || [];

  const addProfileField = () => {
    const currentFields = form.getValues('profileFields') || [];
    form.setValue('profileFields', [...currentFields, { fieldKey: '', label: '', question: '', required: false }]);
  };

  const removeProfileField = index => {
    const currentFields = form.getValues('profileFields') || [];
    form.setValue(
      'profileFields',
      currentFields.filter((_, i) => i !== index)
    );
  };

  const fieldOptions = availableFields
    .filter(f => f.systemField !== SystemFieldType.QrToken)
    .map(f => ({
      id: fieldFullName(f),
      name: f.label,
    }));

  const handleFieldChange = (index, selectedFieldKey) => {
    const selectedField = availableFields.find(f => fieldFullName(f) === selectedFieldKey);
    if (selectedField) {
      form.setValue(`profileFields.${index}.label`, selectedField.label);
      form.setValue(`profileFields.${index}.question`, `Введите ${selectedField.label.toLowerCase()}`);
    }
  };

  return (
    <CardBlock title={'Поля профиля для нетворкинга'} form={form} save={handleSave}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Button startIcon={<AddIcon />} onClick={addProfileField} variant='outlined' size='small'>
            Добавить поле
          </Button>
        </Grid>

        {profileFields.map((field, index) => (
          <Grid size={12} key={index}>
            <Grid container spacing={2} alignItems='center'>
              <Grid size={3}>
                <Controller
                  control={form.control}
                  name={`profileFields.${index}.fieldKey`}
                  defaultValue={''}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label='Поле'
                      select
                      fullWidth
                      margin='dense'
                      variant='outlined'
                      helperText={fieldState.error?.message}
                      error={!!fieldState.error}
                      onChange={(e) => {
                        field.onChange(e);
                        handleFieldChange(index, e.target.value);
                      }}
                    >
                      {fieldOptions.map(option => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={3}>
                <ECTextField
                  name={`profileFields.${index}.label`}
                  form={form}
                  initial={settings}
                  label='Метка'
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <ECTextField
                  name={`profileFields.${index}.question`}
                  form={form}
                  initial={settings}
                  label='Вопрос'
                  fullWidth
                />
              </Grid>
              <Grid size={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.required}
                      onChange={e => form.setValue(`profileFields.${index}.required`, e.target.checked)}
                    />
                  }
                  label='Обяз.'
                />
              </Grid>
              <Grid size={1}>
                <IconButton onClick={() => removeProfileField(index)} color='error'>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </CardBlock>
  );
};

const LocationAndSlotsSection = ({ form, settings, handleSave }) => {
  const locationAndSlots = form.watch('locationAndSlots') || [];

  const addLocation = () => {
    const currentLocations = form.getValues('locationAndSlots') || [];
    form.setValue('locationAndSlots', [
      ...currentLocations,
      { locationName: '', slotDurationMinutes: 15, startTime: '09:00', endTime: '18:00' },
    ]);
  };

  const removeLocation = index => {
    const currentLocations = form.getValues('locationAndSlots') || [];
    form.setValue(
      'locationAndSlots',
      currentLocations.filter((_, i) => i !== index)
    );
  };

  return (
    <CardBlock title={'Локации для оффлайн встреч'} form={form} save={handleSave}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Button startIcon={<AddIcon />} onClick={addLocation} variant='outlined' size='small'>
            Добавить локацию
          </Button>
        </Grid>

        {locationAndSlots.map((location, index) => (
          <Grid size={12} key={index}>
            <Grid container spacing={2} alignItems='center'>
              <Grid size={3}>
                <ECTextField
                  name={`locationAndSlots.${index}.locationName`}
                  form={form}
                  initial={settings}
                  label='Название'
                  fullWidth
                />
              </Grid>
              <Grid size={2}>
                <ECTextField
                  name={`locationAndSlots.${index}.startTime`}
                  form={form}
                  initial={settings}
                  label='Время открытия'
                  type='time'
                  placeholder='09:00'
                  fullWidth
                />
              </Grid>
              <Grid size={2}>
                <ECTextField
                  name={`locationAndSlots.${index}.endTime`}
                  form={form}
                  initial={settings}
                  label='Время закрытия'
                  // type='time'
                  placeholder='18:00'
                  fullWidth
                />
              </Grid>
              <Grid size={2}>
                <ECTextField
                  name={`locationAndSlots.${index}.slotDurationMinutes`}
                  form={form}
                  initial={settings}
                  label='Интервал бронирования'
                  type='number'
                  fullWidth
                />
              </Grid>
              <Grid size={1}>
                <IconButton onClick={() => removeLocation(index)} color='error'>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </CardBlock>
  );
};

// EV-85: контакты по умолчанию — как у бота при взаимном лайке (см. ParticipantCards на сервере)
const DEFAULT_CONTACT_FIELDS = ['email', 'phoneNumber', 'telegram'];

const WebNetworkingSection = ({ form, settings, availableFields, handleSave }) => {
  const webEnabled = form.watch('webEnabled');
  const contactFields = form.watch('contactFields') || settings.contactFields || DEFAULT_CONTACT_FIELDS;
  const link = settings.webToken ? `${window.location.origin}/app/net/${settings.webToken}` : null;

  const contactOptions = [
    ...availableFields
      .filter(f => [SystemFieldType.Email, SystemFieldType.PhoneNumber].includes(f.systemField) || (!f.systemField && f.name))
      .map(f => ({ id: fieldFullName(f), name: f.label })),
    { id: 'telegram', name: 'Telegram (если участник пользуется ботом)' },
  ];

  const toggleContact = id => {
    const next = contactFields.includes(id) ? contactFields.filter(k => k !== id) : [...contactFields, id];
    form.setValue('contactFields', next, { shouldDirty: true });
  };

  return (
    <CardBlock title={'Веб-версия для участников'} form={form} save={handleSave}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Typography variant='body2' color='textSecondary'>
            Отдельная страница нетворкинга без Telegram: участник входит по почте, заполняет анкету, смотрит анкеты
            других и запрашивает контакты. Работает на общем состоянии с ботом (сценарии «Простой» и «Tinder»).
          </Typography>
        </Grid>
        <Grid size={12}>
          <Controller
            control={form.control}
            name='webEnabled'
            defaultValue={settings.webEnabled || false}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} />}
                label='Включить веб-версию'
              />
            )}
          />
          {webEnabled && selectedScenarioIsSmart(form) && (
            <Typography variant='body2' color='error'>
              Веб-версия не поддерживает сценарий Smart Lab — выберите «Простой» или «Tinder».
            </Typography>
          )}
        </Grid>
        {link && (
          <Grid size={12}>
            <FormLabel component='legend'>Ссылка для участников</FormLabel>
            <Grid container spacing={2} alignItems='center'>
              <Grid size={{ xs: 12, md: 9 }}>
                <TextField value={link} fullWidth margin='dense' InputProps={{ readOnly: true }} />
                <Button size='small' onClick={() => navigator.clipboard && navigator.clipboard.writeText(link)}>
                  Скопировать ссылку
                </Button>
                <Button size='small' href={link} target='_blank' rel='noopener noreferrer'>
                  Открыть
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <a href={'/api/qr?qrToken=' + encodeURIComponent(link)} target='_blank' rel='noopener noreferrer'>
                  <img src={'/api/qr?qrToken=' + encodeURIComponent(link)} alt='QR-код ссылки' style={{ width: 140 }} />
                </a>
              </Grid>
            </Grid>
          </Grid>
        )}
        {!link && webEnabled && (
          <Grid size={12}>
            <Typography variant='body2' color='textSecondary'>
              Ссылка для участников появится после сохранения.
            </Typography>
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 4 }}>
          <ECPhotoField name='webLogo' label='Логотип мероприятия' form={form} initial={settings} height={140} />
          <Typography variant='body2' color='textSecondary'>
            Показывается на странице участника рядом с названием мероприятия. Лучше PNG с прозрачным фоном.
          </Typography>
        </Grid>
        <Grid size={12}>
          <ECTextField
            name='introText'
            form={form}
            initial={settings}
            label='Текст на первой странице'
            placeholder='Расскажите участникам, зачем заполнять анкету и как устроен обмен контактами'
            multiline
            minRows={3}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <FormLabel component='legend'>Контакты — открываются только после того, как участник примет запрос</FormLabel>
          {contactOptions.map(o => (
            <FormControlLabel
              key={o.id}
              control={<Checkbox checked={contactFields.includes(o.id)} onChange={() => toggleContact(o.id)} />}
              label={o.name}
              sx={{ display: 'flex' }}
            />
          ))}
        </Grid>
        {webEnabled && <PwaSettings form={form} settings={settings} />}
      </Grid>
    </CardBlock>
  );
};

// EV-88: лимиты Android/iOS — подпись под иконкой без обрезки и иконка, которая не «мылит»
const PWA_NAME_MAX = 12;
const PWA_ICON_MIN = 512;

const checkPwaIcon = file =>
  new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ok = img.naturalWidth === img.naturalHeight && img.naturalWidth >= PWA_ICON_MIN;
      resolve(
        ok
          ? null
          : `Нужна квадратная картинка не меньше ${PWA_ICON_MIN}×${PWA_ICON_MIN}, а загружена ${img.naturalWidth}×${img.naturalHeight}`
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve('Не удалось прочитать картинку — загрузите PNG или JPG');
    };
    img.src = url;
  });

export const pwaSettingsErrors = data => {
  if (!data.webEnabled || !data.pwaEnabled) {
    return {};
  }
  const errors = {};
  if (!data.pwaIcon) {
    errors.pwaIcon = 'Загрузите иконку приложения';
  }
  const name = (data.pwaName || '').trim();
  if (!name) {
    errors.pwaName = 'Введите название приложения';
  } else if (name.length > PWA_NAME_MAX) {
    errors.pwaName = `Не длиннее ${PWA_NAME_MAX} символов`;
  }
  return errors;
};

const PwaSettings = ({ form, settings }) => {
  const pwaEnabled = form.watch('pwaEnabled');
  const pwaName = form.watch('pwaName') || '';
  const pwaIcon = form.watch('pwaIcon');
  const nameError = form.formState.errors.pwaName;

  return (
    <>
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid size={12}>
        <Controller
          control={form.control}
          name='pwaEnabled'
          defaultValue={settings.pwaEnabled || false}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} />}
              label='Мобильное приложение'
            />
          )}
        />
        <Typography variant='body2' color='textSecondary'>
          Участники смогут установить страницу нетворкинга на телефон как приложение — со своей иконкой и названием.
          На странице появится кнопка «Установить приложение».
        </Typography>
      </Grid>
      {pwaEnabled && (
        <>
          <Grid size={{ xs: 12, md: 4 }}>
            <ECPhotoField
              name='pwaIcon'
              label='Иконка приложения *'
              form={form}
              initial={settings}
              height={140}
              validateFile={checkPwaIcon}
            />
            <Typography variant='body2' color='textSecondary'>
              Квадрат PNG или JPG, не меньше {PWA_ICON_MIN}×{PWA_ICON_MIN} (лучше 1024×1024), без прозрачного фона.
              Важное держите в центре — Android может скруглить или обрезать края.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ECTextField
              name='pwaName'
              form={form}
              initial={settings}
              label='Название приложения *'
              placeholder='Форум 2026'
              slotProps={{ htmlInput: { maxLength: PWA_NAME_MAX } }}
              helperText={nameError?.message || `Подпись под иконкой: ${pwaName.length}/${PWA_NAME_MAX} символов`}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormLabel component='legend'>Так будет на телефоне</FormLabel>
            <div style={{ width: 88, marginTop: 8, textAlign: 'center' }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  margin: '0 auto',
                  borderRadius: 14,
                  overflow: 'hidden',
                  background: '#f0f0f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                }}
              >
                {pwaIcon && <img src={'/api/image/' + pwaIcon} alt='' style={{ width: '100%', height: '100%' }} />}
              </div>
              <Typography variant='caption' noWrap component='div' sx={{ mt: 0.5 }}>
                {pwaName || 'Название'}
              </Typography>
            </div>
          </Grid>
        </>
      )}
    </>
  );
};

const selectedScenarioIsSmart = form => form.watch('scenario') === NetworkingScenarioType.SmartLab;

const Networking = props => {
  const appContext = useContext(AppContext);
  const { mid: meetupId } = useParams();

  const [settings, setSettings] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const form = useForm();
  const selectedScenario = form.watch('scenario');

  useEffect(() => {
    Promise.all([appContext.networkingApi.getNetworkingSettings(meetupId), appContext.configApi.getSettings(meetupId)])
      .then(([{ data: networkingData }, { data: configData }]) => {
        setSettings(networkingData);
        setAvailableFields(configData.fields.fields);
        form.reset(networkingData);
      })
      .catch(appContext.errorHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetupId]);

  const handleSave = data => {
    const pwaErrors = pwaSettingsErrors(data);
    if (Object.keys(pwaErrors).length) {
      Object.entries(pwaErrors).forEach(([name, message]) => form.setError(name, { type: 'validate', message }));
      return;
    }
    appContext.networkingApi
      .updateNetworkingSettings(meetupId, data)
      .then(({ data: newData }) => {
        setSettings(newData);
        form.reset(newData);
      })
      .catch(appContext.errorHandler);
  };

  const handleEnabledChange = event => {
    const newSettings = { ...settings, enabled: event.target.checked };
    setSettings(newSettings);
    appContext.networkingApi.updateNetworkingSettings(meetupId, newSettings).catch(appContext.errorHandler);
  };

  if (!settings || !availableFields.length) {
    return null;
  }

  return (
    <Root className={classes.root}>
      <div className={classes.topBar}>
        <FormControlLabel
          control={<Switch checked={settings.enabled} onChange={handleEnabledChange} color='secondary' />}
          label='Включить модуль нетворкинга'
        />
      </div>

      <CardBlock title={'Сценарий нетворкинга'} form={form} save={handleSave}>
        <Grid container spacing={3}>
          <Grid size={3}>
            <FormLabel component='legend'>Тип сценария:</FormLabel>
            <ECRadioGroupField
              name='scenario'
              form={form}
              initial={settings}
              options={[
                { id: NetworkingScenarioType.Simple, name: 'Простой' },
                { id: NetworkingScenarioType.Tinder, name: 'Tinder' },
                { id: NetworkingScenarioType.SmartLab, name: 'Smart Lab' },
              ]}
            />
          </Grid>
          
          <Grid size={9}>
            {selectedScenario === NetworkingScenarioType.Simple && (
              <Typography variant="body2" color="textSecondary" style={{ paddingTop: '8px' }}>
                <strong>Простой сценарий</strong><br/>
                Классический формат нетворкинга с открытым доступом ко всем участникам. Пользователи могут свободно просматривать профили других участников, отправлять сообщения и планировать встречи без ограничений и алгоритмов подбора.
              </Typography>
            )}
            {selectedScenario === NetworkingScenarioType.Tinder && (
              <Typography variant="body2" color="textSecondary" style={{ paddingTop: '8px' }}>
                <strong>Tinder сценарий</strong><br/>
                Интерактивный формат с системой взаимных лайков и алгоритмической подборкой участников. Пользователи оценивают профили свайпами, и встречи происходят только при взаимной заинтересованности сторон для эффективного нетворкинга.
              </Typography>
            )}
            {selectedScenario === NetworkingScenarioType.SmartLab && (
              <Typography variant="body2" color="textSecondary" style={{ paddingTop: '8px' }}>
                <strong>Smart Lab сценарий</strong><br/>
                Интеллектуальная система подбора на основе профессиональных интересов и целей участников. Алгоритм анализирует компетенции, предпочтения и задачи для автоматического формирования оптимальных пар и групп участников.
              </Typography>
            )}
            {!selectedScenario && (
              <Typography variant="body2" color="textSecondary" style={{ paddingTop: '8px', fontStyle: 'italic' }}>
                Выберите тип сценария для просмотра описания
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardBlock>

      <ProfileFieldsSection form={form} settings={settings} availableFields={availableFields} handleSave={handleSave} />
      <WebNetworkingSection form={form} settings={settings} availableFields={availableFields} handleSave={handleSave} />
      {selectedScenario === NetworkingScenarioType.SmartLab && (
        <LocationAndSlotsSection form={form} settings={settings} handleSave={handleSave} />
      )}
    </Root>
  );
};

export default Networking;
